import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  CreditTransactionDetailModal,
  UsdWalletTransactionDetailModal,
  usePortalLedgerDetailState,
  PAGE,
  MONEY,
} from '@kioskgaming/ui';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  ChevronRight,
  MonitorSmartphone,
  Search,
  UserPlus,
  X,
} from 'lucide-react';
import AppLayout from '../components/layout/AppLayout';
import ShopWalletCard from '../components/dashboard/ShopWalletCard';
import DashboardAnalytics from '../components/dashboard/DashboardAnalytics';
import ModalPortal from '../components/ui/ModalPortal';
import ActivePlayersOnlyCheckbox from '../components/ActivePlayersOnlyCheckbox';
import { ShopPlayerLinkByPhonePanel } from '../components/ShopPlayerLinkByPhonePanel';
import { useAuth } from '../hooks/useAuth';
import {
  fetchDashboardV2Overview,
  fetchShopCreditTransactionById,
  fetchShopHome,
  fetchShopUsdLedgerEntryById,
  searchShopHome,
} from '../services/api';
import type {
  ShopHomeData,
  ShopHomeSearchData,
  ShopHomeSearchPlayer,
  ShopHomeSearchTransaction,
} from '../types';

type MoneyIntent = 'deposit' | 'redeem' | null;

const EMPTY_SEARCH: ShopHomeSearchData = {
  q: '',
  players: [],
  creditTransactions: [],
  usdTransactions: [],
};

function formatMoney(n: number): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<ShopHomeSearchData>(EMPTY_SEARCH);
  const [home, setHome] = useState<ShopHomeData | null>(null);
  const [homeLoading, setHomeLoading] = useState(true);
  const [homeError, setHomeError] = useState<string | null>(null);
  const [todayFlow, setTodayFlow] = useState<{ moneyIn: number; moneyOut: number } | null>(null);
  const [wallet, setWallet] = useState<{
    creditBalance: number;
    creditAvailable: number;
    usdBalanceNet: number;
  } | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [intent, setIntent] = useState<MoneyIntent>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(0);

  const ledgerDetail = usePortalLedgerDetailState({
    fetchCreditById: async (id) => {
      const res = await fetchShopCreditTransactionById(id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    fetchUsdById: async (id) => {
      const res = await fetchShopUsdLedgerEntryById(id);
      if (!res.success || !res.data?.item) throw new Error(res.message || 'Not found');
      return res.data.item;
    },
    onUnavailable: () =>
      toast.error('Related transaction is unavailable or outside your permission.'),
  });

  /** The debt banner's "Buy credits now" lands here with this state and opens the purchase dialog once. */
  const openCreditPurchaseFromNav =
    (location.state as { openCreditPurchase?: boolean } | null)?.openCreditPurchase === true;

  useEffect(() => {
    if (!openCreditPurchaseFromNav) return;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
  }, [openCreditPurchaseFromNav, location.pathname, location.search, navigate]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setHomeLoading(true);
    setHomeError(null);
    fetchShopHome()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setHome(res.data);
        } else {
          setHomeError(res.message || 'Could not load home');
        }
      })
      .catch(() => {
        if (!cancelled) setHomeError('Could not load home');
      })
      .finally(() => {
        if (!cancelled) setHomeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadOverviewPulse = useCallback(async () => {
    setWalletLoading(true);
    try {
      const data = await fetchDashboardV2Overview('today');
      setWallet({
        creditBalance: data.kpis.creditBalance?.value ?? 0,
        creditAvailable: data.kpis.creditAvailable?.value ?? 0,
        usdBalanceNet: data.kpis.usdBalanceNet?.value ?? 0,
      });
    } catch {
      /* wallet pulse is optional; today row comes from /shop/home */
    } finally {
      setWalletLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOverviewPulse();
  }, [loadOverviewPulse]);

  useEffect(() => {
    if (home?.today) {
      setTodayFlow({ moneyIn: home.today.moneyIn, moneyOut: home.today.moneyOut });
    }
  }, [home]);

  useEffect(() => {
    if (debounced.length < 2) {
      setResults(EMPTY_SEARCH);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    searchShopHome(debounced, { activeOnly })
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setResults(res.data);
        else setResults({ ...EMPTY_SEARCH, q: debounced });
      })
      .catch(() => {
        if (!cancelled) setResults({ ...EMPTY_SEARCH, q: debounced });
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, activeOnly]);

  const hasQuery = debounced.length >= 2;
  const resultCount =
    results.players.length + results.creditTransactions.length + results.usdTransactions.length;

  const startMoneyIntent = (next: Exclude<MoneyIntent, null>) => {
    setIntent(next);
    searchRef.current?.focus();
  };

  const openPlayer = useCallback(
    (player: ShopHomeSearchPlayer, action: MoneyIntent = intent) => {
      const q =
        action === 'deposit' ? '?action=deposit' : action === 'redeem' ? '?action=redeem' : '';
      navigate(`/players/${player.id}${q}`);
    },
    [intent, navigate],
  );

  const openCashier = () => {
    const url = home?.cashierPortalUrl?.trim();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate('/cashiers');
  };

  const problems = home?.problems;
  const problemRows = useMemo(() => {
    if (!problems) return [];
    return [
      {
        key: 'withdrawals',
        label: 'Pending withdrawals',
        count: problems.pendingWithdrawals.count,
        to: problems.pendingWithdrawals.link,
      },
      {
        key: 'wallets',
        label: 'Failed game wallets',
        count: problems.failedGameWallets.count,
        to: problems.failedGameWallets.link,
      },
      {
        key: 'exceptions',
        label: PAGE.shiftExceptions,
        count: problems.cashierExceptions.count,
        to: problems.cashierExceptions.link,
      },
      {
        key: 'balance',
        label: problems.lowBalance.active
          ? `Low credit balance (≤ ${formatMoney(problems.lowBalance.thresholdUsd)})`
          : 'Low credit balance',
        count: problems.lowBalance.active ? 1 : 0,
        to: problems.lowBalance.link,
        alert: problems.lowBalance.active,
      },
    ];
  }, [problems]);

  return (
    <AppLayout>
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-stone-900 sm:text-2xl">{PAGE.dashboard}</h1>
            <p className="mt-1 text-sm text-stone-500">Find a Player, move money, or clear exceptions.</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Player name, phone, email, or transaction ID"
              className="w-full rounded-2xl border border-stone-200 bg-white py-3.5 pl-12 pr-4 text-base text-stone-900 outline-none placeholder:text-stone-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
              aria-label="Global search"
            />
          </div>
          <ActivePlayersOnlyCheckbox checked={activeOnly} onChange={setActiveOnly} className="sm:shrink-0" />
        </div>

        {intent ? (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            {intent === 'deposit'
              ? 'Search for a Player to deposit to.'
              : 'Search for a Player to redeem for.'}
            <button
              type="button"
              className="ml-2 font-semibold underline underline-offset-2"
              onClick={() => setIntent(null)}
            >
              Cancel
            </button>
          </p>
        ) : null}

        {hasQuery ? (
          <section className="rounded-2xl border border-stone-200 bg-white p-4 sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-stone-900">Results</h2>
              <span className="text-xs text-stone-500">
                {searching ? 'Searching…' : `${resultCount} match${resultCount === 1 ? '' : 'es'}`}
              </span>
            </div>
              {!searching && resultCount === 0 ? (
                <p className="py-8 text-center text-sm text-stone-500">No matches for “{debounced}”.</p>
              ) : (
                <div className="space-y-4">
                  {results.players.length > 0 ? (
                    <ResultGroup title="Players">
                      {results.players.map((p) => (
                        <div
                          key={p.id}
                          className="flex flex-col gap-2 border-b border-stone-100 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <button
                            type="button"
                            onClick={() => openPlayer(p)}
                            className="min-w-0 text-left"
                          >
                            <p className="truncate font-semibold text-stone-900">{p.username || p.phone || p.email || p.id}</p>
                            <p className="truncate text-xs text-stone-500">
                              {[p.phone, p.email].filter(Boolean).join(' · ') || 'No contact'}
                              {` · ${formatMoney(p.balance)} credits`}
                            </p>
                          </button>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => openPlayer(p, null)}
                              className="rounded-lg border border-stone-200 px-2.5 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-50"
                            >
                              Detail
                            </button>
                            <button
                              type="button"
                              onClick={() => openPlayer(p, 'deposit')}
                              className="rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                            >
                              {MONEY.deposit}
                            </button>
                            <button
                              type="button"
                              onClick={() => openPlayer(p, 'redeem')}
                              className="rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                            >
                              {MONEY.redeem}
                            </button>
                          </div>
                        </div>
                      ))}
                    </ResultGroup>
                  ) : null}
                  {results.creditTransactions.length > 0 ? (
                    <ResultGroup title={PAGE.creditTransactions}>
                      {results.creditTransactions.map((tx) => (
                        <TxRow
                          key={`c-${tx.id}`}
                          tx={tx}
                          opening={ledgerDetail.relatedLoading === 'credit'}
                          onDetail={() => void ledgerDetail.openCreditById(tx.id)}
                        />
                      ))}
                    </ResultGroup>
                  ) : null}
                  {results.usdTransactions.length > 0 ? (
                    <ResultGroup title={PAGE.usdWalletTransactions}>
                      {results.usdTransactions.map((tx) => (
                        <TxRow
                          key={`u-${tx.id}`}
                          tx={tx}
                          opening={ledgerDetail.relatedLoading === 'usd'}
                          onDetail={() => void ledgerDetail.openUsdById(tx.id)}
                        />
                      ))}
                    </ResultGroup>
                  ) : null}
                </div>
              )}
            </section>
          ) : null}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <ActionTile
            icon={ArrowDownToLine}
            title="Deposit to a Player"
            hint="Deposit credits at the counter"
            onClick={() => startMoneyIntent('deposit')}
            selected={intent === 'deposit'}
            tone="emerald"
          />
          <ActionTile
            icon={ArrowUpFromLine}
            title="Redeem for a Player"
            hint="Redeem credits and pay cash"
            onClick={() => startMoneyIntent('redeem')}
            selected={intent === 'redeem'}
            tone="amber"
          />
          <ActionTile
            icon={UserPlus}
            title="Create Player"
            hint="Register or link an account"
            onClick={() => {
              setCreateKey((k) => k + 1);
              setCreateOpen(true);
            }}
            selected={createOpen}
            tone="sky"
          />
          <ActionTile
            icon={MonitorSmartphone}
            title="Open Cashier"
            hint={home?.cashierPortalUrl ? 'Opens Cashier portal' : 'Manage Cashiers'}
            onClick={openCashier}
            tone="violet"
          />
        </div>

        <ShopWalletCard
          creditBalance={wallet?.creditBalance ?? home?.problems.lowBalance.creditBalance ?? 0}
          creditAvailable={wallet?.creditAvailable ?? home?.problems.lowBalance.creditBalance ?? 0}
          usdBalanceNet={wallet?.usdBalanceNet ?? home?.problems.lowBalance.usdBalanceNet ?? 0}
          isLoading={walletLoading && !wallet}
          costRate={
            user?.costRate != null && Number.isFinite(Number(user.costRate)) ? Number(user.costRate) : 0
          }
          tone="emerald"
          defaultOpenCreditPurchase={openCreditPurchaseFromNav}
          onBalancesRefresh={loadOverviewPulse}
          onOrdered={() => void loadOverviewPulse()}
        />

        <section className="rounded-2xl border border-stone-200 bg-white px-4 py-3">
          <div className="grid grid-cols-2 divide-x divide-stone-100">
            <TodayStat
              label="Counter deposits today"
              hint="Counter cash received"
              value={todayFlow?.moneyIn}
              loading={!todayFlow && homeLoading}
              to={home?.today?.linkIn || '/cash-settlement/deposits'}
              tone="in"
            />
            <TodayStat
              label="Counter redemptions today"
              hint="Counter cash paid out"
              value={todayFlow?.moneyOut}
              loading={!todayFlow && homeLoading}
              to={home?.today?.linkOut || '/cash-settlement/redemptions'}
              tone="out"
            />
          </div>
        </section>

          <section className="rounded-2xl border border-stone-200 bg-white p-4 sm:p-5">
            <div className="mb-3">
              <h2 className="inline-flex items-center gap-2 text-sm font-semibold text-stone-900">
                <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden />
                Problems
              </h2>
            </div>
            {homeLoading ? (
              <p className="py-8 text-center text-sm text-stone-400">Loading…</p>
            ) : homeError ? (
              <p className="py-6 text-center text-sm text-red-600">{homeError}</p>
            ) : (
              <ul className="-mx-2 divide-y divide-stone-100">
                {problemRows.map((row) => (
                  <li key={row.key}>
                    <Link
                      to={row.to}
                      className="group flex items-center justify-between gap-3 rounded-lg px-2 py-3 text-sm hover:bg-stone-50"
                    >
                      <span className="font-medium text-stone-800">{row.label}</span>
                      <span className="inline-flex shrink-0 items-center gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                            row.alert
                              ? 'bg-red-100 text-red-800'
                              : row.count > 0
                                ? 'bg-amber-100 text-amber-900'
                                : 'bg-stone-100 text-stone-500'
                          }`}
                        >
                          {row.alert ? 'Alert' : row.count}
                        </span>
                        <ChevronRight
                          className="h-4 w-4 text-stone-300 group-hover:text-stone-500"
                          aria-hidden
                        />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

        <DashboardAnalytics />
      </div>

      <CreditTransactionDetailModal
        entry={ledgerDetail.creditDetail}
        onClose={ledgerDetail.closeCredit}
        accountLabel="Your Shop account"
        audience="operator"
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
        resolveUsdConversion={ledgerDetail.resolveUsdConversion}
      />
      <UsdWalletTransactionDetailModal
        row={ledgerDetail.usdDetail}
        onClose={ledgerDetail.closeUsd}
        onOpenCreditSource={ledgerDetail.handleOpenCreditSource}
        onOpenUsdSource={ledgerDetail.handleOpenUsdSource}
        relatedLoading={ledgerDetail.relatedLoading}
      />

      {createOpen ? (
        <ModalPortal>
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-create-player-title"
          >
            <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-4 shadow-xl sm:p-5">
              <div className="mb-3 flex items-start justify-between gap-3 border-b border-stone-100 pb-3">
                <div>
                  <h2 id="home-create-player-title" className="text-base font-semibold text-stone-900">
                    Create Player
                  </h2>
                  <p className="mt-1 text-xs text-stone-500">
                    Register a new Player or link an account that has no Shop yet.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  className="rounded-lg border border-stone-200 p-2 text-stone-600 hover:bg-stone-50"
                  aria-label="Close create Player modal"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <ShopPlayerLinkByPhonePanel
                key={createKey}
                showPanelHeader={false}
                onLinked={() => setCreateOpen(false)}
                onLinkedPlayer={(linked) => {
                  setCreateOpen(false);
                  navigate(`/players/${linked.id}`);
                }}
              />
            </div>
          </div>
        </ModalPortal>
      ) : null}
    </AppLayout>
  );
}

function TodayStat({
  label,
  hint,
  value,
  loading,
  to,
  tone,
}: {
  label: string;
  hint: string;
  value?: number;
  loading: boolean;
  to: string;
  tone: 'in' | 'out';
}) {
  return (
    <Link to={to} className="px-3 py-1 first:pl-0 last:pr-0 hover:opacity-80">
      <p className="text-xs font-medium uppercase tracking-wide text-stone-500">{label}</p>
      <p
        className={`mt-0.5 text-lg font-bold tabular-nums ${
          tone === 'in' ? 'text-emerald-800' : 'text-orange-800'
        }`}
      >
        {loading || value == null ? '…' : `$${formatMoney(value)}`}
      </p>
      <p className="mt-0.5 text-xs text-stone-500">{hint}</p>
    </Link>
  );
}

const ACTION_TONES = {
  emerald: {
    idle: 'border-emerald-200 bg-emerald-50/70 hover:bg-emerald-50',
    selected: 'border-emerald-400 bg-emerald-100 ring-2 ring-emerald-200',
    icon: 'text-emerald-700',
  },
  amber: {
    idle: 'border-amber-200 bg-amber-50/70 hover:bg-amber-50',
    selected: 'border-amber-400 bg-amber-100 ring-2 ring-amber-200',
    icon: 'text-amber-800',
  },
  sky: {
    idle: 'border-sky-200 bg-sky-50/70 hover:bg-sky-50',
    selected: 'border-sky-400 bg-sky-100 ring-2 ring-sky-200',
    icon: 'text-sky-700',
  },
  violet: {
    idle: 'border-violet-200 bg-violet-50/70 hover:bg-violet-50',
    selected: 'border-violet-400 bg-violet-100 ring-2 ring-violet-200',
    icon: 'text-violet-700',
  },
} as const;

function ActionTile({
  icon: Icon,
  title,
  hint,
  onClick,
  selected,
  tone = 'emerald',
}: {
  icon: typeof Search;
  title: string;
  hint: string;
  onClick: () => void;
  selected?: boolean;
  tone?: keyof typeof ACTION_TONES;
}) {
  const colors = ACTION_TONES[tone];

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl border p-4 text-left transition ${selected ? colors.selected : colors.idle}`}
    >
      <Icon className={`mb-2 h-5 w-5 ${colors.icon}`} aria-hidden />
      <p className="text-sm font-semibold text-stone-900">{title}</p>
      <p className="mt-0.5 text-xs text-stone-500">{hint}</p>
    </button>
  );
}

function ResultGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-stone-500">{title}</p>
      {children}
    </div>
  );
}

function TxRow({
  tx,
  opening,
  onDetail,
}: {
  tx: ShopHomeSearchTransaction;
  opening: boolean;
  onDetail: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-stone-100 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between">
      <button type="button" onClick={onDetail} className="min-w-0 text-left">
        <p className="truncate font-mono text-xs font-semibold text-stone-900">{tx.transactionId || tx.id}</p>
        <p className="text-xs capitalize text-stone-500">
          {tx.type || tx.kind}
          {` · ${formatMoney(tx.amount)}`}
        </p>
      </button>
      <button
        type="button"
        onClick={onDetail}
        disabled={opening}
        className="rounded-lg border border-stone-200 px-2.5 py-1.5 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
      >
        {opening ? 'Opening…' : 'Detail'}
      </button>
    </div>
  );
}
